import { cva } from 'class-variance-authority';

/**
 * Divider class recipe.
 *
 * Zero `dark:` variants, as everywhere in this layer: both colours and the thickness come from the
 * `--ds-divider-*` slots that `.ds-divider` sets in `components/styles/design-system.css`. Read the
 * block header there before changing anything visual — in particular why the rule is a translucent
 * FILL rather than a border, which is the one decision in this component that is easy to undo by
 * accident.
 *
 * ── Full-bleed, and the drawn cell disagrees with itself here ─────────────────────────────────────
 *
 * The rule runs edge to edge. The component set's own description says so — "Full-bleed — runs edge
 * to edge rather than inset" — and BOTH variant roots contradict it: each carries 12px of padding
 * (bound to the `x3` spacing variable) on the two ends of its own axis, which insets the `Rule`
 * child to 296px inside a 320px frame.
 *
 * The description wins, and this is a deliberate deviation from the drawing rather than an oversight:
 *
 *   - A stated intent beats a leftover. The padding is what the author wrote a sentence to rule out.
 *   - An inset cannot be composed away. A caller can always inset a full-bleed rule with `mx-*`; a
 *     caller cannot remove 12px that the component insists on, so the inset reading is the one that
 *     makes some designs unreachable.
 *   - 12px is also exactly `List-Item`'s own horizontal padding (node 82:7424), which is the most
 *     likely way the value got there — a rule drawn to line up with a row's text inside the row's
 *     own container, rather than a property of the divider.
 *
 * The padding should come off the Figma cells. Raised with design on 2026-09-17; until it does, this
 * file is the record of which of the two readings shipped.
 *
 * ── A vertical divider has no height of its own ───────────────────────────────────────────────────
 *
 * `self-stretch` rather than `h-full`, and the difference is the whole usability of the vertical
 * cell. In Figma the vertical variant's `Rule` is `FILL` on the counter axis, which is
 * `align-self: stretch` — it takes the height of the flex row it sits in. `h-full` is
 * `height: 100%`, which resolves to zero against a parent with no definite height, so a vertical
 * divider in an ordinary `flex items-center` row would render at 1x0 and be invisible.
 *
 * `shrink-0` on both orientations for the same class of reason: a 1px box inside a flex container
 * that is short on space is the first thing the browser will shrink to nothing.
 *
 * A vertical divider in a NON-flex parent still has no height, and no CSS here can give it one.
 * That is the caller's `className` — the stories show both — and it is why `className` is documented
 * on the props type as carrying the length rather than only placement.
 */
export const dividerRecipe = cva(['ds-divider', 'shrink-0 bg-[var(--ds-divider-rule)]'], {
  variants: {
    /**
     * Which axis the rule runs along. The thickness slot is read on the CROSS axis in both cases,
     * which is what makes one slot serve both cells.
     *
     * `w-full` on the horizontal cell rather than nothing: a `div` is already full-width in block
     * flow, but a divider dropped into a flex COLUMN is a flex item and would hug its own (zero)
     * content width instead. Stating it makes the horizontal cell work in both kinds of parent,
     * which is the mirror of the problem `self-stretch` solves on the vertical one.
     */
    orientation: {
      horizontal: 'h-[var(--ds-divider-thickness)] w-full',
      vertical: 'w-[var(--ds-divider-thickness)] self-stretch',
    },
  },
  defaultVariants: { orientation: 'horizontal' },
});
